import React, { useState } from 'react';
import styled from 'styled-components';
import { FileText, Upload, User, MessageSquare, Send, CheckCircle, AlertCircle } from 'lucide-react';
import ContentLayout from '../../Common/Component/ContentLayout';

const Container = styled.div`
  width: 100%;
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  overflow: hidden;
  max-width: 800px;
  margin: 0 auto;
`;

const FormSection = styled.div`
  padding: 30px;
  border-bottom: 1px solid #e5e7eb;
  
  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
  
  &:invalid {
    border-color: #ef4444;
  }
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Textarea = styled.textarea`
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const FileUploadArea = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  background: #f9fafb;
  transition: all 0.2s;
  cursor: pointer;
  
  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }
  
  &.dragover {
    border-color: #3b82f6;
    background: #eff6ff;
  }
`;

const UploadIcon = styled.div`
  width: 48px;
  height: 48px;
  background: #e5e7eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  color: #6b7280;
`;

const UploadText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 10px 0;
`;

const UploadSubtext = styled.p`
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
`;

const FileList = styled.div`
  margin-top: 15px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: #f3f4f6;
  border-radius: 6px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #374151;
`;

const RemoveButton = styled.button`
  margin-left: auto;
  padding: 2px 8px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    background: #dc2626;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: start;
  gap: 10px;
  margin: 20px 0;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin-top: 2px;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
  cursor: pointer;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 15px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    background: #2563eb;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const InfoBox = styled.div`
  background: ${props => {
    switch(props.type) {
      case 'success': return '#dcfce7';
      case 'warning': return '#fef3c7';
      case 'error': return '#fee2e2';
      default: return '#dbeafe';
    }
  }};
  color: ${props => {
    switch(props.type) {
      case 'success': return '#16a34a';
      case 'warning': return '#d97706';
      case 'error': return '#dc2626';
      default: return '#2563eb';
    }
  }};
  padding: 15px;
  border-radius: 8px;
  margin: 20px 0;
  font-size: 14px;
  display: flex;
  align-items: start;
  gap: 10px;
`;

const QuoteList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 20px;
`;

const QuoteCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  transition: box-shadow 0.2s;
  
  &:hover {
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  }
`;

const QuoteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 15px;
`;

const QuoteTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 5px 0;
`;

const QuoteDate = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0;
`;

const QuoteStatus = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch(props.status) {
      case 'pending': return '#fef3c7';
      case 'in-progress': return '#dbeafe';
      case 'completed': return '#dcfce7';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'pending': return '#d97706';
      case 'in-progress': return '#2563eb';
      case 'completed': return '#16a34a';
      default: return '#6b7280';
    }
  }};
`;

const QuoteContent = styled.p`
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
  margin: 0 0 10px 0;
`;

const QuoteCategory = styled.span`
  font-size: 12px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
`;

function QuoteRequest() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    category: '',
    title: '',
    description: '',
    budget: '',
    deadline: '',
    agreeToTerms: false
  });
  const [files, setFiles] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showMyQuotes, setShowMyQuotes] = useState(false);

  // 더미 견적 요청 목록
  const myQuotes = [
    {
      id: 'QT-2024-001',
      title: '홈페이지 리뉴얼 견적 요청',
      category: '웹개발',
      date: '2024-02-15',
      status: 'in-progress',
      description: '기존 홈페이지를 반응형으로 리뉴얼하고 싶습니다. 관리자 페이지도 포함해서 견적 부탁드립니다.'
    },
    {
      id: 'QT-2024-002',
      title: '모바일 앱 개발',
      category: '앱개발',
      date: '2024-02-10',
      status: 'completed',
      description: 'iOS/Android 쇼핑몰 앱 개발 견적 요청드립니다.'
    },
    {
      id: 'QT-2024-003',
      title: '로고 디자인',
      category: '디자인',
      date: '2024-02-05',
      status: 'pending',
      description: '새로운 브랜드 로고 디자인 작업 견적 요청입니다.'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateFile = (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/jpg'
    ];
    
    if (file.size > maxSize) {
      alert(`${file.name}은 10MB를 초과합니다.`);
      return false;
    }
    
    if (!allowedTypes.includes(file.type)) {
      alert(`${file.name}은 지원되지 않는 파일 형식입니다.`);
      return false;
    }
    
    return true;
  };

  const handleFileUpload = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(validateFile);
    
    if (files.length + validFiles.length > 5) {
      alert('최대 5개의 파일만 업로드할 수 있습니다.');
      return;
    }
    
    setFiles(prev => [...prev, ...validFiles]);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('dragover');
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = droppedFiles.filter(validateFile);
    
    if (files.length + validFiles.length > 5) {
      alert('최대 5개의 파일만 업로드할 수 있습니다.');
      return;
    }
    
    setFiles(prev => [...prev, ...validFiles]);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('dragover');
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('dragover');
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const required = ['name', 'email', 'phone', 'category', 'title', 'description'];
    const missing = required.filter(field => !formData[field] || formData[field].trim() === '');
    
    if (missing.length > 0) {
      alert(`다음 필드를 입력해주세요: ${missing.join(', ')}`);
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('유효한 이메일 주소를 입력해주세요.');
      return false;
    }
    
    const phoneRegex = /^[0-9\-\s+()]+$/;
    if (!phoneRegex.test(formData.phone)) {
      alert('유효한 전화번호를 입력해주세요.');
      return false;
    }
    
    if (formData.description.length < 10) {
      alert('프로젝트 설명을 10자 이상 입력해주세요.');
      return false;
    }
    
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (!formData.agreeToTerms) {
      alert('개인정보 수집 및 이용에 동의해주세요.');
      return;
    }
    
    // 실제로는 서버에 데이터를 전송
    console.log('Form Data:', formData);
    console.log('Files:', files);
    
    setIsSubmitted(true);
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'pending': return '검토중';
      case 'in-progress': return '진행중';
      case 'completed': return '완료';
      default: return '알수없음';
    }
  };

  if (isSubmitted) {
    return (
      <Container>
        <FormContainer>
          <FormSection style={{textAlign: 'center', padding: '60px 30px'}}>
            <div style={{
              width: '80px',
              height: '80px',
              background: '#dcfce7',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              color: '#16a34a'
            }}>
              <CheckCircle size={40} />
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#111827',
              marginBottom: '15px',
              textAlign: 'center'
            }}>견적 요청이 완료되었습니다!</h2>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              lineHeight: '1.6',
              textAlign: 'center',
              margin: '0'
            }}>
              담당자 검토 후 영업일 기준 1-2일 내에 연락드리겠습니다.<br/>
              문의사항이 있으시면 언제든 연락주세요.
            </p>
            <div style={{marginTop: '30px', display: 'flex', gap: '15px', justifyContent: 'center'}}>
              <SubmitButton 
                style={{width: 'auto', padding: '12px 24px'}}
                onClick={() => setIsSubmitted(false)}
              >
                새 견적 요청
              </SubmitButton>
              <SubmitButton 
                style={{width: 'auto', padding: '12px 24px', background: '#6b7280'}}
                onClick={() => setShowMyQuotes(true)}
              >
                내 견적 목록
              </SubmitButton>
            </div>
          </FormSection>
        </FormContainer>
      </Container>
    );
  }

  if (showMyQuotes) {
    return (
      <ContentLayout 
        title="내 견적 요청 목록"
        subtitle="지금까지 요청하신 견적들을 확인할 수 있습니다"
        centerTitle={false}
      >
        <Container>

        <FormContainer>
          <FormSection>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <SectionTitle>
                <FileText size={20} />
                견적 요청 목록
              </SectionTitle>
              <SubmitButton 
                style={{width: 'auto', padding: '8px 16px', fontSize: '14px'}}
                onClick={() => setShowMyQuotes(false)}
              >
                새 견적 요청
              </SubmitButton>
            </div>

            <QuoteList>
              {myQuotes.map(quote => (
                <QuoteCard key={quote.id}>
                  <QuoteHeader>
                    <div>
                      <QuoteTitle>{quote.title}</QuoteTitle>
                      <QuoteDate>{quote.date} 요청</QuoteDate>
                    </div>
                    <QuoteStatus status={quote.status}>
                      {getStatusText(quote.status)}
                    </QuoteStatus>
                  </QuoteHeader>
                  <QuoteContent>{quote.description}</QuoteContent>
                  <QuoteCategory>{quote.category}</QuoteCategory>
                </QuoteCard>
              ))}
            </QuoteList>
          </FormSection>
        </FormContainer>
        </Container>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout 
      title="견적 의뢰"
      subtitle="프로젝트 견적이 필요하시나요? 상세한 정보를 알려주시면 정확한 견적을 제공해드립니다."
      centerTitle={false}
    >
      <Container>

      <FormContainer>
        <form onSubmit={handleSubmit}>
          <FormSection>
            <SectionTitle>
              <User size={20} />
              기본 정보
            </SectionTitle>
            <FormGrid>
              <FormGroup>
                <Label>이름 *</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="이름을 입력하세요"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>이메일 *</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="이메일을 입력하세요"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>연락처 *</Label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="연락처를 입력하세요"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>회사명</Label>
                <Input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="회사명을 입력하세요"
                />
              </FormGroup>
            </FormGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <MessageSquare size={20} />
              프로젝트 정보
            </SectionTitle>
            <FormGrid>
              <FormGroup>
                <Label>카테고리 *</Label>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">카테고리를 선택하세요</option>
                  <option value="web">웹개발</option>
                  <option value="app">앱개발</option>
                  <option value="design">디자인</option>
                  <option value="marketing">마케팅</option>
                  <option value="consulting">컨설팅</option>
                  <option value="other">기타</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>예산 범위</Label>
                <Select
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                >
                  <option value="">예산을 선택하세요</option>
                  <option value="under-100">100만원 미만</option>
                  <option value="100-300">100만원 - 300만원</option>
                  <option value="300-500">300만원 - 500만원</option>
                  <option value="500-1000">500만원 - 1,000만원</option>
                  <option value="over-1000">1,000만원 이상</option>
                </Select>
              </FormGroup>
              <FormGroup style={{gridColumn: '1/-1'}}>
                <Label>프로젝트 제목 *</Label>
                <Input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="프로젝트 제목을 입력하세요"
                  required
                />
              </FormGroup>
              <FormGroup style={{gridColumn: '1/-1'}}>
                <Label>희망 완료일</Label>
                <Input
                  type="date"
                  name="deadline"
                  value={formData.deadline}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup style={{gridColumn: '1/-1'}}>
                <Label>프로젝트 상세 설명 *</Label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="프로젝트에 대한 상세한 설명을 입력하세요"
                  required
                />
              </FormGroup>
            </FormGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <Upload size={20} />
              첨부 파일
            </SectionTitle>
            <FileUploadArea
              onDrop={handleFileDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => document.getElementById('fileInput').click()}
            >
              <UploadIcon>
                <Upload size={24} />
              </UploadIcon>
              <UploadText>클릭하거나 파일을 드래그하여 업로드</UploadText>
              <UploadSubtext>PDF, DOC, XLS, PPT, 이미지 파일 (최대 10MB, 5개 제한)</UploadSubtext>
            </FileUploadArea>
            <input
              id="fileInput"
              type="file"
              multiple
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif"
            />
            {files.length > 0 && (
              <FileList>
                {files.map((file, index) => (
                  <FileItem key={index}>
                    <FileText size={16} />
                    {file.name} ({(file.size / 1024).toFixed(1)}KB)
                    <RemoveButton onClick={() => removeFile(index)}>
                      삭제
                    </RemoveButton>
                  </FileItem>
                ))}
              </FileList>
            )}
          </FormSection>

          <FormSection>
            <InfoBox type="info">
              <AlertCircle size={16} />
              <div>
                견적 요청 후 담당자가 검토하여 영업일 기준 1-2일 내에 연락드립니다.<br/>
                급한 문의사항은 고객센터(02-1234-5678)로 직접 연락주세요.
              </div>
            </InfoBox>

            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                id="agreeToTerms"
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleInputChange}
              />
              <CheckboxLabel htmlFor="agreeToTerms">
                개인정보 수집 및 이용에 동의합니다. (필수)<br/>
                <span style={{color: '#6b7280', fontSize: '12px'}}>
                  입력하신 정보는 견적 제공 목적으로만 사용되며, 완료 후 안전하게 폐기됩니다.
                </span>
              </CheckboxLabel>
            </CheckboxGroup>

            <div style={{display: 'flex', gap: '15px'}}>
              <SubmitButton type="submit" disabled={!formData.agreeToTerms}>
                <Send size={16} />
                견적 요청하기
              </SubmitButton>
              <SubmitButton 
                type="button"
                style={{background: '#6b7280', flex: '0 0 auto', width: 'auto', padding: '15px 30px'}}
                onClick={() => setShowMyQuotes(true)}
              >
                내 견적 목록
              </SubmitButton>
            </div>
          </FormSection>
        </form>
      </FormContainer>
      </Container>
    </ContentLayout>
  );
}

export default QuoteRequest;